import { addDoc, deleteDoc, doc, getDoc, getDocs, query, updateDoc } from 'firebase/firestore';
import { getUnitCost, getUnitCostSale, Input, inputsRef } from '@api/Inputs/Input';
import InputCategoryServ from '@api/InputCategories/InputCategoryServ';
import { InputCategory } from '@api/InputCategories/InputCategory';
import DistributionCenterInventoryServ from '@api/DistributionCenterInventories/DistributionCenterInventoryServ';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import { buildCreationStamp } from '@api/ActivityLogs/CreationStamp';
import { INPUT_FIELDS, logCatalogChange } from '@api/ActivityLogs/CatalogActivity';
import DistributionCenterServ from '@api/DistributionCenters/DistributionCenterServ';
import { publishCatalog } from '@api/Catalog/CatalogVersion';

export default {
    async saveInput(input: Input): Promise<Input> {
        input.unitCost = getUnitCost(input);
        input.unitCostSale = getUnitCostSale(input);
        input.merma = input.merma ?? 0;
        const isNew = !input.id;
        const before = isNew ? null : await this.getInput(input.id as string);
        if (isNew) {
            const data = await addDoc(inputsRef, { ...input, ...(await buildCreationStamp()) });
            input.id = data.id;
        }
        await updateDoc(doc(inputsRef, input.id), { ...input });
        await publishCatalog('input');
        // Los costos del insumo alimentan el margen de TODO el catálogo.
        await logCatalogChange('input', input.id, before, input, INPUT_FIELDS);
        await this.syncInputAcrossDistributionCenters(input);
        //await this.cleanupAllDistributionCenterInventories();
        return input;
    },
    async syncInputAcrossDistributionCenters(input: Input) {
        if (!input.id) return;

        console.log(`[Sync] Starting sync for input: ${input.name} (${input.id})`);

        const isAvailableInAll = input.distributionCentersAvailable?.includes('all') ?? true;
        const allCenters = await DistributionCenterServ.getDistributionCenters();
        const targetCenterIds = isAvailableInAll
            ? allCenters.map((dc) => dc.id!)
            : input.distributionCentersAvailable || [];

        console.log(`[Sync] Target centers for ${input.name}:`, targetCenterIds);

        const existingInventories = await DistributionCenterInventoryServ.getInventoriesByInputId(input.id);

        // Update or add to target centers
        for (const centerId of targetCenterIds) {
            const existing = existingInventories.find((inv) => inv.distributionCenterId === centerId);
            if (existing && existing.id) {
                await DistributionCenterInventoryServ.updateInputInventory(existing.id, input, existing.stock);
                console.log(`[Sync] Updated inventory for center: ${centerId}`);
            } else {
                await DistributionCenterInventoryServ.saveInventory({
                    distributionCenterId: centerId,
                    input,
                    stock: 0
                });
                console.log(`[Sync] Created new inventory for center: ${centerId} with 0 stock`);
            }
        }

        // Remove from centers that are no longer in the target list
        for (const inventory of existingInventories) {
            if (!targetCenterIds.includes(inventory.distributionCenterId) && inventory.id) {
                // Pass the inventory with its current stock to force newStock to be 0 and trigger deletion
                await DistributionCenterInventoryServ.deleteInventory({
                    ...inventory,
                    stock: inventory.stock
                });
                console.log(`[Sync] Removed inventory from center: ${inventory.distributionCenterId}`);
            }
        }
        console.log(`[Sync] Finished sync for input: ${input.name}`);
    },
    async cleanupAllDistributionCenterInventories() {
        console.log('[Cleanup] Starting global inventory cleanup...');
        const inputs = await this.getInputs();
        const validInputIds = new Set(inputs.map((input) => input.id).filter(Boolean));
        console.log(`[Cleanup] Found ${inputs.length} valid global inputs.`);

        // 1. Remove orphaned inventories (inputs that no longer exist globally)
        console.log('[Cleanup] Checking for orphaned inventories...');
        const allInventories = await DistributionCenterInventoryServ.getAllInventories();
        let orphanedCount = 0;
        for (const inventory of allInventories) {
            if (inventory.input.id && !validInputIds.has(inventory.input.id) && inventory.id) {
                console.log(
                    `[Cleanup] Removing orphaned inventory for input ID: ${inventory.input.id} in center: ${inventory.distributionCenterId}`
                );
                await DistributionCenterInventoryServ.deleteInventory({
                    ...inventory,
                    stock: inventory.stock
                });
                orphanedCount++;
            }
        }
        console.log(`[Cleanup] Removed ${orphanedCount} orphaned inventory records.`);

        // 2. Synchronize existing global inputs
        let processedCount = 0;
        for (const input of inputs) {
            if (input.id) {
                await this.syncInputAcrossDistributionCenters(input);
                processedCount++;
                console.log(`[Cleanup] Progress: ${processedCount}/${inputs.length} inputs processed.`);
            }
        }
        console.log('[Cleanup] Global inventory cleanup completed successfully!');
    },
    async getInputs(): Promise<Input[]> {
        const inputs: Input[] = [];
        const queryDoc = query(inputsRef);
        const snapshot = await getDocs(queryDoc);
        if (snapshot.empty) {
            return inputs;
        }
        snapshot.docs.map((doc) => {
            const input = doc.data() as Input;
            input.id = doc.id;
            inputs.push(input);
        });
        // ⚠️ Antes esto era un N+1: un `getDoc` de categoría POR INSUMO, así que
        // `getInputs()` costaba 254 lecturas en vez de 127 — y lo pagaban el POS
        // en frío y 5 pantallas de catálogo e inventario. Las categorías son ~10
        // docs: se leen de UNA query y se resuelven en memoria.
        const categories = await InputCategoryServ.getInputCategories();
        const categoriesById = new Map(categories.map((category) => [category.id, category]));
        inputs.forEach((input) => {
            if (input.inputCategoryId) {
                input.inputCategoryData = categoriesById.get(input.inputCategoryId) as InputCategory;
            }
        });
        return inputs;
    },
    async getInput(inputId: string): Promise<Input> {
        const documentData = await getDoc(doc(inputsRef, inputId));
        const input = documentData.data() as Input;
        if (input) {
            input.id = inputId;
        }
        return input;
    },
    async deleteInput(inputId: string): Promise<void> {
        // First, delete from all distribution center inventories
        const inventories = await DistributionCenterInventoryServ.getInventoriesByInputId(inputId);
        for (const inventory of inventories) {
            if (inventory.id) {
                await DistributionCenterInventoryServ.deleteInventory({
                    ...inventory,
                    stock: inventory.stock
                });
            }
        }
        // Then delete the global input
        await deleteDoc(doc(inputsRef, inputId));
        await publishCatalog('input-delete');
        await ActivityLogServ.addActivityLog('input', inputId, 'eliminó el insumo');
        return;
    }
};
